import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { toast } from 'react-toastify';
import { observer } from 'mobx-react';
import format from 'string-template';
import { autorun } from 'mobx';
import moment from 'moment';

import Lead, {
  LeadCallOutcome,
  SalesStage,
} from '../../../../../Model/Lead';
import Promotion from '../../../../../Model/Promotion';
import CoachesListContext from '../../../../../context/CoachesListContext';
import { StyledTabs } from '../../../../../pages/MealsPlanner/styles';
import ContactInfoItem from '../../../../ClientInfo/components/ContactInfoItem';
import { ErrorTag, NoticeTag } from '../../../../../../components/Tags';
import {
  withMsgEndpointContextProvider,
} from '../../../../../context/MsgEndpointContext';
import {
  withContractModalContextProvider,
} from '../../../../ContractModal/context/withContractModalContext';
import {
  StyledEmailIcon,
  StyledPhoneIcon,
  StyledTimeZoneIcon,
} from '../../../../ClientInfo/components/UserInfoSection/styles';
import useToast from '../../../../../hooks/useToast';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';
import salesStageTexts from '../../../salesStages.json';
import { DateFormat } from '../../../../../../utils/date';
import { SendMode } from '../../ChatModal/utils';
import LogActivity from './components/LogActivity';
import ContractActionButtons from './components/LeadModalContractActionButtons';
import ActionButtons from './components/ActionButtons';
import {
  ContactInfoSection,
  PricingInstructionSection,
  PricingInstructionText,
  LeadInfoSection,
  Title,
  TitleSection,
  Container,
  TabHeaderRow,
  TopSection,
  TabContentContainer,
  StyledLeadModalDialog,
  CoachLabel,
} from './styles';
import {
  getLeadModalTabsConfig,
  leadModalTabs,
} from './utils';
import texts from './texts';

const EMPTY_CELL = '-';

const STAGE_CHANGE_TOAST_OPTIONS = {
  autoClose: 60000,
  closeOnClick: false,
  position: 'top-center',
};

const LeadsModal = ({
  lead,
  isInsideSalesView,
  isSDRChat,
  onClose,
  showModal,
  userId,
  userName,
  requireOutcome: requireOutcomeProp,
  selectedTab: selectedTabProp,
  initialMessageFilter,
}) => {
  const [selectedTab, setSelectedTab] = useState(selectedTabProp);
  const [requireOutcome, setRequireOutcome] = useState(requireOutcomeProp);
  const [activePromotions, setActivePromotions] = useState([]);
  const [historyCollection, setHistoryCollection] = useState(null);
  const [history, setHistory] = useState([]);
  const [draftMessage, setDraftMessage] = useState('');
  // The chat draft outlives the Chat tab (S2-7241), so the channel it is addressed to has to
  // outlive it as well. Kept here rather than in ChatModal, which unmounts on every tab change:
  // a note written in the Comment channel must not come back armed as an outbound message.
  const [draftSendMode, setDraftSendMode] = useState(SendMode.TEXT);
  const [stageInitialOutcome, setStageInitialOutcome] = useState(null);
  const stageToastIdRef = useRef(null);
  const leadSalesStage = lead?.salesStage;
  const leadFollowUpDate = lead?.followUpDate;

  const isComponentMountedRef = useComponentMounted();
  const { coachesList } = useContext(CoachesListContext);
  const { showToast } = useToast();
  const { salesStage } = lead;
  const prevSalesStageRef = useRef(salesStage);

  const dismissStageChangeToast = useCallback(() => {
    const id = stageToastIdRef.current;
    if (id != null) {
      toast.dismiss(id);
      stageToastIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (showModal) {
      prevSalesStageRef.current = salesStage;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  useEffect(() => {
    const prevStage = prevSalesStageRef.current;
    prevSalesStageRef.current = salesStage;

    // Only show a toast if the lead is inside sales view and the stage actually changed.
    if (!isInsideSalesView || prevStage === salesStage) {
      return;
    }

    if (salesStage === SalesStage.WON) {
      setRequireOutcome(true);
      dismissStageChangeToast();
      stageToastIdRef.current = toast.success(texts.leadWonRecorded, STAGE_CHANGE_TOAST_OPTIONS);
      setStageInitialOutcome(LeadCallOutcome.WON);
    } else if (salesStage === SalesStage.PAYMENT_PENDING) {
      setRequireOutcome(true);
      dismissStageChangeToast();
      stageToastIdRef.current = toast.warning(
        texts.leadPaymentPendingRecorded,
        STAGE_CHANGE_TOAST_OPTIONS,
      );
      setStageInitialOutcome(LeadCallOutcome.PAYMENT_PENDING);
    }
  }, [salesStage, isInsideSalesView, dismissStageChangeToast]);

  useEffect(() => {
    if (!showModal) {
      dismissStageChangeToast();
      setStageInitialOutcome(null);
    }
  }, [showModal, dismissStageChangeToast]);

  useEffect(() => {
    const loadHistory = async () => {
      if (lead) {
        const historyCol = await lead.getHistory();
        if (isComponentMountedRef.current) {
          setHistoryCollection(historyCol);
        }
      }
    };
    loadHistory();
  }, [isComponentMountedRef, lead]);

  useEffect(() => {
    if (historyCollection) {
      const disposer = autorun(() => {
        setHistory(historyCollection.docs.slice());
      });
      return () => disposer();
    }
    return () => {};
  }, [historyCollection]);

  useEffect(() => {
    const loadPromotions = async () => {
      try {
        const collection = await Promotion.getCurrentPromotionsByCoachId(lead.coach);
        if (isComponentMountedRef.current) {
          const activePromotionsDocs = collection.docs.filter((doc) => doc.isOngoing() && !doc.isDeleted);
          setActivePromotions(activePromotionsDocs);
        }
      } catch (error) {
        showToast(format(texts.errorFetchingPromotions, error), { error: true });
      }
    };
    if (isInsideSalesView) {
      loadPromotions();
    }
  }, [isComponentMountedRef, showToast, isInsideSalesView, lead]);

  const tabsConfig = useMemo(() => getLeadModalTabsConfig(isInsideSalesView, activePromotions.length > 0),
    [isInsideSalesView, activePromotions]);

  const salesStageTagText = useMemo(() => {
    if (leadSalesStage !== SalesStage.FOLLOW_UP || !leadFollowUpDate) {
      return salesStageTexts[leadSalesStage] || EMPTY_CELL;
    }

    const followUpDateText = moment(leadFollowUpDate.toDate())
      .format(DateFormat.DATE_MONTH_YEAR_TIME_SHORT_DISPLAY_FORMAT);

    return format(texts.followUpScheduledAt, { followUpDateText });
  }, [leadFollowUpDate, leadSalesStage]);

  const tabContent = useMemo(() => {
    const { component: TabContent, props } = tabsConfig[selectedTab];
    const chatProps = selectedTab === leadModalTabs.CHAT ? {
      message: draftMessage,
      onMessageChange: setDraftMessage,
      sendMode: draftSendMode,
      onSendModeChange: setDraftSendMode,
      initialMessageFilter,
    } : {};

    return (
      <TabContent
        lead={lead}
        showPostCommitmentConfiguration={isInsideSalesView}
        coachId={lead.coach}
        history={history}
        activePromotions={activePromotions}
        isSDRChat={isSDRChat}
        {...chatProps}
        {...props}
      />
    );
  }, [
    selectedTab,
    lead,
    isInsideSalesView,
    tabsConfig,
    activePromotions,
    history,
    isSDRChat,
    draftMessage,
    draftSendMode,
    initialMessageFilter,
  ]);

  const onOutcomeSaved = useCallback(() => {
    setRequireOutcome(false);
    dismissStageChangeToast();
    setStageInitialOutcome(null);
  }, [dismissStageChangeToast]);

  const modalActionButtons = useMemo(() => {
    if (selectedTab === leadModalTabs.CHAT) {
      // The chat component handles its own footer
      return null;
    }
    if (selectedTab === leadModalTabs.CONTRACT) {
      return (
        !isSDRChat ? <ContractActionButtons showSendLinkOption={isInsideSalesView} /> : null
      );
    }
    if (selectedTab === leadModalTabs.HISTORY) {
      return (
        <LogActivity lead={lead} />
      );
    }
    return (
      <ActionButtons
        lead={lead}
        isInsideSalesView={isInsideSalesView}
        onCallSuccess={() => setRequireOutcome(true)}
        onRescheduleSuccess={() => setRequireOutcome(false)}
        userId={userId}
        userName={userName}
        onCloseLeadModal={onClose}
        history={history}
        initialOutcome={stageInitialOutcome}
        onOutcomeSaved={onOutcomeSaved}
      />
    );
  }, [
    lead,
    isInsideSalesView,
    selectedTab,
    onClose,
    setRequireOutcome,
    userId,
    userName,
    isSDRChat,
    history,
    stageInitialOutcome,
    onOutcomeSaved,
  ]);

  const onCloseModal = useCallback(() => {
    if (requireOutcome) {
      showToast(texts.outcomeNotRecorded, { error: true });
    } else {
      onClose();
      setDraftMessage('');
    }
  }, [requireOutcome, showToast, onClose, setDraftMessage]);

  const isShowActionButtons = (isInsideSalesView || selectedTab !== leadModalTabs.LEAD_DETAILS)
    && selectedTab !== leadModalTabs.CHAT;

  const content = (
    <Container $isSDRChat={isSDRChat}>
      <TopSection>
        <LeadInfoSection>
          <TitleSection>
            <Title>
              {lead.name || EMPTY_CELL}
            </Title>
            {isInsideSalesView && <ErrorTag>{salesStageTagText}</ErrorTag>}
            <NoticeTag>
              {texts.coachLabel}
              <CoachLabel>{coachesList[lead.coach] || EMPTY_CELL}</CoachLabel>
            </NoticeTag>
          </TitleSection>
          <ContactInfoSection>
            <ContactInfoItem icon={<StyledPhoneIcon />}>
              {lead?.phoneNumber || EMPTY_CELL}
            </ContactInfoItem>
            <ContactInfoItem icon={<StyledEmailIcon />}>
              {lead?.email || EMPTY_CELL}
            </ContactInfoItem>
            <ContactInfoItem icon={<StyledTimeZoneIcon />}>
              {`${lead?.timezone || EMPTY_CELL} (${lead?.country || EMPTY_CELL})`}
            </ContactInfoItem>
            {isInsideSalesView && (
              <ContactInfoItem icon="IS">
                {lead?.msgServerContactNo || lead?.sendBlueName || EMPTY_CELL}
              </ContactInfoItem>
            )}
          </ContactInfoSection>
        </LeadInfoSection>
        {isInsideSalesView && !!lead.pricingInstruction && (
          <PricingInstructionSection>
            <PricingInstructionText>
              {lead.pricingInstruction}
            </PricingInstructionText>
          </PricingInstructionSection>
        )}
        <TabHeaderRow>
          <StyledTabs
            tabsConfig={tabsConfig}
            selectedTab={selectedTab}
            onSelectTab={setSelectedTab}
          />
        </TabHeaderRow>
      </TopSection>
      <TabContentContainer>
        {tabContent}
      </TabContentContainer>
    </Container>
  );

  if (isSDRChat) {
    return content;
  }

  return (
    <StyledLeadModalDialog
      open={showModal}
      onClose={onCloseModal}
      fullWidth
      maxWidth="md"
      actionButtons={isShowActionButtons ? modalActionButtons : null}
    >
      {content}
    </StyledLeadModalDialog>
  );
};

LeadsModal.propTypes = {
  lead: PropTypes.instanceOf(Lead).isRequired,
  onClose: PropTypes.func,
  showModal: PropTypes.bool,
  userId: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
  isInsideSalesView: PropTypes.bool,
  isSDRChat: PropTypes.bool,
  requireOutcome: PropTypes.bool,
  selectedTab: PropTypes.string,
  initialMessageFilter: PropTypes.string,
};

LeadsModal.defaultProps = {
  isInsideSalesView: false,
  isSDRChat: false,
  requireOutcome: false,
  selectedTab: leadModalTabs.LEAD_DETAILS,
  onClose: () => {},
  showModal: false,
  initialMessageFilter: null,
};

export default compose(
  withMsgEndpointContextProvider,
  withContractModalContextProvider,
  observer,
)(LeadsModal);
